import { createPortal } from "preact/compat";
import { h, Fragment } from "preact";
import { t } from "../../../_i18n";
import { useEffect, useRef, useState } from "preact/hooks";

import { handleSafeClick } from "../../../utils";
import './helper-tooltip.scss';

interface Props {
  card: boolean;
  children: JSX.Element;
  onClose?: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  position?: "top" | "bottom";
  step?: number;
  subtitle: string;
  title: string;
  totalSteps?: number;
  visible: boolean;
}

export const HelperTooltip = ({
  card = false,
  children,
  onClose,
  onNext,
  onPrev,
  position = "bottom",
  step = 1,
  subtitle,
  title,
  totalSteps = 1,
  visible,
}: Props) => {
  /** Refs **/
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  /** States **/
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const [ready, setReady] = useState(false);
  const [widthStyle, setWidthStyle] = useState<string>("100%");

  /** Lifecycles **/
  useEffect(() => {
    if (card && window.innerWidth < 550) {
      setWidthStyle("47%");
    } else {
      setWidthStyle("100%");
    }
  }, [window.innerWidth]);

  useEffect(() => {
    setTimeout(() => {
      updatePosition();
      window.addEventListener("scroll", updatePosition, { passive: true });
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition);
        window.removeEventListener("resize", updatePosition);
      };
    }, 100);
  }, []);

  useEffect(() => {
    if (!visible) {
      setReady(false);
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updatePosition();
        setReady(true);
      });
    });

    window.addEventListener("scroll", updatePosition, { passive: true });
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible]);

  /** Methods **/
  const updatePosition = () => {
    const el = wrapperRef.current?.children[0] as HTMLElement | undefined;
    const tooltip = tooltipRef.current as HTMLElement | null;

    if (!el) return;

    const rect = el.getBoundingClientRect();

    const tooltipHeight = tooltip ? tooltip.offsetHeight : 0;
    const gap = 16;

    setPos({
      top:
        position === "top"
          ? rect.top + window.scrollY - tooltipHeight - gap
          : rect.top + window.scrollY + rect.height + gap,
      left: rect.left,
      width: rect.width,
    });
  };

  /** Render **/
  return (
    <Fragment>
      <div ref={wrapperRef} className="d-flex" style={{ width: widthStyle }}>
        {children}
      </div>

      {visible &&
        createPortal(
          <div
            ref={tooltipRef}
            class={`helper__tooltip fade-in ${position === "top" ? "top" : ""}`}
            style={{
              top: pos.top,
              left: pos.left,
              width: pos.width,
              opacity: ready ? 1 : 0,
              pointerEvents: ready ? "auto" : "none",
            }}
          >
            <div class="helper__tooltip__arrow" />
            <h2>{title}</h2>
            <p>{subtitle}</p>
            <div className="d-flex flex-center-y flex-space-between gap-1 helper__buttons-container">
              <span>
                {t("Paso")} {step} de {totalSteps}
              </span>
              <div className="d-flex helper__buttons gap-1">
                <button
                  onClick={handleSafeClick(onPrev, step === 1)}
                  className="btn btn-secondary mr-2"
                  disabled={step === 1}
                >
                  {t("Atras")}
                </button>
                {step !== totalSteps ? (
                  <button onClick={onNext} className="btn btn-primary">
                    {t("Siguiente")}
                  </button>
                ) : (
                  <button onClick={onClose} className="btn btn-primary">
                    {t("Finalizar")}
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </Fragment>
  );
};
